import { api } from '@udm/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { request } from '../../api/client';
import { Card, ProtocolTag } from '../../ui/components';
import { formatNumber } from '../../ui/format';

const KEY = ['settings', 'protocols'] as const;
const parse = (v: unknown) => api.ProtocolSettings.parse(v);

/**
 * Settings → Communication protocols (super admin): presence windows per protocol profile, the global "gateway
 * unreachable → UNKNOWN" window, and what we learned about each protocol. Saved values reach the connected gateways
 * immediately (gateway.config).
 */
export function ProtocolsCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: KEY,
    queryFn: () => request('GET', '/api/v1/settings/protocols', undefined, parse),
  });
  const saveProfile = useMutation({
    mutationFn: (p: { key: string; offline_after_s: number | null; downstream_poll_s?: number | null }) =>
      request(
        'PUT',
        `/api/v1/settings/protocols/${encodeURIComponent(p.key)}`,
        {
          offline_after_s: p.offline_after_s,
          ...(p.downstream_poll_s !== undefined ? { downstream_poll_s: p.downstream_poll_s } : {}),
        },
        parse,
      ),
    onSuccess: (d) => qc.setQueryData(KEY, d),
  });
  const saveGlobal = useMutation({
    mutationFn: (s: number) =>
      request('PUT', '/api/v1/settings/presence', { gateway_unknown_after_s: s }, parse),
    onSuccess: (d) => qc.setQueryData(KEY, d),
  });
  const data = q.data;
  return (
    <Card title={t('settings.protocols.title')}>
      <p className="muted" style={{ marginTop: 0 }}>
        {t('settings.protocols.hint')}
      </p>
      {data && (
        <>
          <table className="table protocols-table">
            <thead>
              <tr>
                <th>{t('settings.protocols.protocol')}</th>
                <th className="num">{t('settings.protocols.devices')}</th>
                <th>{t('settings.protocols.offlineAfter')}</th>
                <th title={t('settings.protocols.downstreamPollHint')}>
                  {t('settings.protocols.downstreamPoll')}
                </th>
                <th>{t('settings.protocols.learned')}</th>
              </tr>
            </thead>
            <tbody>
              {data.profiles.map((p) => (
                <tr key={p.key}>
                  <td>
                    <ProtocolTag protocol={p.key} /> <span className="muted small">{p.name}</span>
                  </td>
                  <td className="num">{formatNumber(p.devices)}</td>
                  <td>
                    <SecondsField
                      value={p.offline_after_s}
                      placeholder={t('settings.protocols.default')}
                      busy={saveProfile.isPending}
                      onSave={(v) => saveProfile.mutate({ key: p.key, offline_after_s: v })}
                    />
                  </td>
                  <td>
                    <SecondsField
                      value={p.downstream_poll_s ?? null}
                      min={300}
                      placeholder={t('settings.protocols.never')}
                      busy={saveProfile.isPending}
                      onSave={(v) =>
                        saveProfile.mutate({
                          key: p.key,
                          offline_after_s: p.offline_after_s,
                          downstream_poll_s: v,
                        })
                      }
                    />
                  </td>
                  <td className="small">
                    {Object.entries(p.learned).map(([k, v]) => (
                      <div key={k}>
                        <span className="muted">{k}:</span> {String(v)}
                      </div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <label className="toolbar" style={{ marginTop: 'var(--space-4)' }}>
            <span>{t('settings.protocols.gatewayUnknown')}</span>
            <SecondsField
              value={data.gateway_unknown_after_s}
              min={15}
              busy={saveGlobal.isPending}
              onSave={(v) => v !== null && saveGlobal.mutate(v)}
            />
          </label>
          {(saveProfile.isSuccess || saveGlobal.isSuccess) && (
            <p className="muted small" role="status">
              {t('settings.protocols.saved')}
            </p>
          )}
        </>
      )}
    </Card>
  );
}

function SecondsField({
  value,
  onSave,
  busy,
  placeholder,
  min = 30,
}: {
  value: number | null;
  onSave: (v: number | null) => void;
  busy: boolean;
  placeholder?: string;
  min?: number;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(value === null ? '' : String(value));
  useEffect(() => setDraft(value === null ? '' : String(value)), [value]);
  const n = draft.trim() === '' ? null : Number(draft);
  const valid = n === null ? placeholder !== undefined : Number.isInteger(n) && n >= min && n <= 86_400;
  const dirty = n !== value;
  return (
    <span className="toolbar">
      <input
        className="input"
        style={{ width: 100 }}
        inputMode="numeric"
        value={draft}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value.replace(/\D/g, ''))}
        aria-invalid={!valid}
      />
      <button type="button" className="btn" disabled={!dirty || !valid || busy} onClick={() => onSave(n)}>
        {t('settings.protocols.save')}
      </button>
    </span>
  );
}
